import {
  confirmSignIn,
  fetchAuthSession,
  signIn,
  signOut,
  signUp
} from "aws-amplify/auth";
import Cookies from "js-cookie";
/**
 * Custom error class for authentication errors
 */
class AuthError extends Error {
  constructor(message, code) {
    super(message);
    this.name = "AuthError";
    this.code = code;
  }
}
/**
 * Initiates the sign-in or sign-up process by sending an OTP to the user's email.
 * If the user doesn't exist, it creates a new user before sending the OTP.
 * @param {string} email - The user's email address.
 * @returns {Promise<object>} A promise that resolves with the auth data if successful.
 */
const authenticate = async (email, authType = "OTP") => {
  try {
    const response = await signIn({
      username: email,
      options: {
        authFlowType: "CUSTOM_WITHOUT_SRP",
        clientMetadata: { authType: authType }
      }
    });
    if (
      response.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE"
    ) {
      await confirmSignIn({
        challengeResponse: "blank",
        options: { clientMetadata: { challengeType: authType } }
      });
      return { challengeName: "CUSTOM_CHALLENGE_", user: { username: email } };
    }
    return response;
  } catch (error) {
    if (error.name === "UserNotFoundException") {
      try {
        await signUp({
          username: email,
          password: Math.random().toString(36),
          options: {
            authFlowType: "CUSTOM_WITHOUT_SRP",
            userAttributes: {
              email,
              name: email.split("@")[0] // Use part of email as name
            }
          }
        });
        return authenticate(email);
      } catch (signUpError) {
        throw new AuthError(signUpError.message, "AuthenticationError");
      }
    } else if (error.name === "UserAlreadyAuthenticatedException") {
      const { tokens } = await fetchAuthSession({ forceRefresh: true });
      return tokens;
    } else {
      //console.log("Error authenticating:", error);
      throw new AuthError(error.message, error.name);
    }
  }
};
/**
 * Verifies the OTP entered by the user.
 * @param {string} otp - The one-time password entered by the user.
 * @returns {Promise<object>} A promise that resolves with the user session if successful.
 */
const verifyOTP = async (answer, challengeType = "OTP") => {
  try {
    await confirmSignIn({
      challengeResponse: answer,
      options: { clientMetadata: { challengeType: challengeType } }
    });
    const { tokens } = await fetchAuthSession({ forceRefresh: true });
    return tokens;
  } catch (error) {
    throw new AuthError(error.message, "OTPVerificationError");
  }
};
/**
 * Resends the OTP to the user's email.
 * @param {string} email - The user's email address.
 * @returns {Promise<object>} A promise that resolves when the OTP has been resent.
 */
const resendOTP = (email) => {
  return authenticate(email);
};
/**
 * Logs out the current user.
 */
const logout = async () => {
  try {
    await signOut();
    localStorage.clear();
    sessionStorage.clear();
  } catch (error) {
    throw new AuthError(error.message, "LogoutError");
  }
};

/**
 * Validate authentication
 * @param {object} payload - The payload from the authentication.
 * @returns {boolean} A boolean that indicates if the authentication is valid.
 */
const validateAuthentication = async (payload) => {
  //check if payload do not have cognito:groups
  if (!payload["cognito:groups"]) {
    return false;
  }

  //check if administrator group is in the payload
  if (payload["cognito:groups"].includes("admin")) {
    return true;
  }

  //return false
  return false;
};

/**
 * Get user token
 * @returns {string} The user token.
 */
const getUserToken = async () => {
  const { tokens } = await fetchAuthSession({ forceRefresh: true });
  if (!tokens?.idToken) {
    // The Cognito session is gone (e.g. refresh token expired) while the app's
    // "logedIn" cookie still says we're signed in: end the session and go to sign-in.
    // Without the cookie we are already on the sign-in flow, so don't redirect.
    if (!Cookies.get("logedIn")) {
      throw new AuthError("User is not signed in.", "NotSignedIn");
    }
    try {
      await logout();
    } catch (error) {
      // ignore - we are redirecting anyway
    }
    Cookies.remove("logedIn");
    window.location.assign(`${process.env.PUBLIC_URL || ""}/`);
    throw new AuthError(
      "Your session has expired. Please sign in again.",
      "SessionExpired"
    );
  }
  return tokens;
};

export {
  authenticate,
  AuthError,
  logout,
  resendOTP,
  verifyOTP,
  validateAuthentication,
  getUserToken
};
